import API from 'utils/api';
import { onSuccess, onError } from 'utils/handlers';
import {
  updateEntidadInfo,
  updateUserInfo,
  updatePeriodoFiscalSelected,
  selectPeriodoFiscalSelected,
  handleLogin,
} from 'store/auth';
import store from 'store/store';
import { patchUsuario } from 'api/usuarios/usuarios';
import { setTheme, setSidebarHidden, setMenuOrder } from 'store/app';
import { defSigob, themesAsArray } from 'themes';
import { getSidebarConfig } from 'api/configuraciones/sidebarConfig';
import { getMenuOrder } from 'api/configuraciones/menuOrder';

/**
 * GET Info Usuario
 * @returns {Promise<object|boolean>}
 */
export const getUserInfo = async (dispatch, access) => {
  try {
    const headers = {};
    if (access) {
      headers.authorization = `Bearer ${access}`;
    }
    const response = await API.get('usuarios/id', { headers });
    const {
      entidad_municipal,
      entidad,
      periodo_fiscal,
      ...userInfo
    } = response.data;
    const periodoSelected = selectPeriodoFiscalSelected(store.getState());
    if (periodoSelected?.vigente && periodoSelected?.id !== periodo_fiscal?.id) {
      periodoSelected.vigente = false;
    } else if (!periodoSelected?.vigente && periodoSelected?.id === periodo_fiscal?.id) {
      periodoSelected.vigente = true;
    }
    if (dispatch) {
      dispatch(updateUserInfo(userInfo));
      dispatch(updateEntidadInfo(entidad_municipal || entidad));
      dispatch(updatePeriodoFiscalSelected(periodoSelected));
    }
    return response.data;
  } catch (err) {
    onError(err);
    return false;
  }
};

/**
 * POST Login
 * @param {object} values
 * @returns {Promise<boolean>}
 */
export const postLogin = async (dispatch, values) => {
  try {
    const response = await API.post('usuarios/login', values);
    const { access, refresh } = response.data;
    const userInfo = await getUserInfo(null, access);
    if (userInfo.entidad_municipal || userInfo.entidad) {
      const theme = themesAsArray.find((t) => t.name === userInfo.tema) || defSigob;
      dispatch(setTheme(theme));
      dispatch(handleLogin({
        token: access,
        refreshToken: refresh,
        ...userInfo,
      }));
      const [sidebarConfig, menuOrder] = await Promise.all([
        getSidebarConfig(),
        getMenuOrder(),
      ]);
      dispatch(setSidebarHidden(sidebarConfig?.hidden_modules));
      dispatch(setMenuOrder(menuOrder?.menu_structure));
    }
    return true;
  } catch (err) {
    onError(err);
    return false;
  }
};

/**
 * PATCH Roles Usuario
 * @param {number} id
 * @param {object} values
 * @returns {Promise<object|boolean>}
 */
export const patchRolesUsuario = async (id, groups) => {
  try {
    const response = await API.patch(`usuarios/agregar-roles/${id}`, { groups });
    return response.data;
  } catch (err) {
    onError(err);
    return false;
  }
};

/**
 * PATCH Permisos Usuario
 * @param {number} id
 * @param {Array<number>} user_permissions
 * @returns {Promise<object|boolean>}
 */
export const patchPermisosUsuario = async (id, user_permissions) => {
  try {
    const response = await API.patch(`usuarios/agregar-permisos/${id}`, { user_permissions });
    return response.data;
  } catch (err) {
    onError(err);
    return false;
  }
};

/**
 * PATCH Password
 * @param {number} id
 * @param {object} values
 * @returns {Promise<object|boolean>}
 */
export const patchPassword = async (values) => {
  try {
    const response = await API.patch('usuarios/actualizar-contrasena/', values);
    onSuccess(response, 'Contraseña actualizada correctamente');
    return response.data;
  } catch (err) {
    onError(err);
    return false;
  }
};

/**
 * PATCH Theme
 * @param {number} userID
 * @param {object} theme
 * @returns {Promise<object|boolean>}
 */
export const patchTheme = async (userID, theme, dispatch) => {
  const response = await patchUsuario(userID, { theme: theme.name });
  if (response) {
    dispatch(setTheme(theme));
  }
  return response.data;
};

/**
 * POST Solicitud cambio de contraseña
 * @param {object} values
 * @param {string} values.email
 * @returns {Promise<object|boolean>}
 */
export const postSolicitudCambioPassword = async (values) => {
  try {
    const response = await API.post('usuarios/solicitud-restablecer-contrasena/', values);
    onSuccess(response, 'Vínculo para restablecer contraseña enviado');
    return response.data;
  } catch (err) {
    onError(err);
    return false;
  }
};

/**
 * GET Verificar cuenta
 * @param {string} codigo
 * @returns {Promise<object|boolean>}
 */
export const getVerificarCuenta = async (codigo) => {
  try {
    const response = await API.post(`usuarios/verificar-email?codigo=${codigo}/`);
    onSuccess(response, 'Cuenta verificada correctamente');
    return response.data;
  } catch (err) {
    onError(err);
    return false;
  }
};

/**
 * GET Cancelar solicitud cambio de contraseña
 * @param {string} codigo
 * @returns {Promise<object|boolean>}
 */
export const getCancelarRestablecerPassword = async (codigo) => {
  try {
    const response = await API.get(`usuarios/cancelar-restablecer-contrasena?codigo=${codigo}`);
    onSuccess(response, 'Cuenta verificada correctamente');
    return response.data;
  } catch (err) {
    onError(err);
    return false;
  }
};

/**
 * POST Solicitud cambio de contraseña
 * @param {string} codigo
 * @param {object} values
 * @returns {Promise<object|boolean>}
 */
export const postRestablecerPassword = async (codigo, values) => {
  try {
    const response = await API.post(`usuarios/restablecer-contrasena?codigo=${codigo}/`, values);
    onSuccess(response, 'Contraseña restablecida correctamente');
    return response.data;
  } catch (err) {
    onError(err);
    return false;
  }
};
